import { css } from '@emotion/css';

import { EDGE_HITBOX_PX } from '../drag/hit-zone.js';

export const BAR_HEIGHT = 24;
export const BAR_VERTICAL_PADDING = 8;

// Empty-area click in a row's timeline band starts a date-drag on that
// existing (unscheduled) row — the bar-renderer host needs to receive
// pointerdown for that, so the whole area opts back into pointer events.
// Individual bars + hotspots already set their own `pointerEvents: auto`,
// so nothing downstream regresses.
export const barContainerStyle = css({
  position: 'absolute',
  inset: 0,
  display: 'block',
  zIndex: 1,
  pointerEvents: 'auto',
});

// Outer chip — owns the position, background + shadow but keeps
// `overflow: visible` so the link hotspot (which sits half outside the
// right edge) isn't clipped.
//
// `&&&[data-classification]` doubles the class selector twice + attr =
// specificity (0,4,0). Keeps classification styling ahead of scoped
// database viewport theme boundaries. V2 button vars are theme-stable.
export const barStyle = css({
  position: 'absolute',
  height: `${BAR_HEIGHT}px`,
  borderRadius: '4px',
  cursor: 'pointer',
  userSelect: 'none',
  pointerEvents: 'auto',
  boxShadow: 'var(--streamlain-shadow-1)',
  overflow: 'visible',
  boxSizing: 'border-box',

  '&&&[data-classification]': {
    background: 'var(--streamlain-v2-button-primary)',
    color: 'var(--streamlain-v2-button-pureWhiteText)',
  },

  // Descendants (label span, progress fill, badges) need the same
  // foreground as the classified bar. Force it on children too
  // (spec 0,4,0 via `&&&[attr] *`).
  '&&&[data-classification] *': {
    color: 'var(--streamlain-v2-button-pureWhiteText)',
  },

  '&&&[data-classification=invalid-range]': {
    background: 'transparent',
    color: 'var(--streamlain-text-primary-color)',
    borderColor: 'var(--streamlain-error-color)',
    borderStyle: 'solid',
    borderWidth: '1px',
  },

  '&&&[data-classification=invalid-range] *': {
    color: 'var(--streamlain-text-primary-color)',
  },

  // Multi-select highlight — the bar renderer adds `data-selected` when
  // the row is part of `selectedRowIds`. The ring + lift read the same
  // regardless of theme (spec 0,4,0 still beats the viewport override
  // at 0,2,1).
  '&&&[data-selected]': {
    outline: '2px solid var(--streamlain-primary-color)',
    outlineOffset: '1px',
    boxShadow: 'var(--streamlain-shadow-2)',
  },

  // Shift-pressed anywhere in the gantt host — bars turn into
  // "add-to-selection" targets, mirroring the shared Table/Gantt
  // cursor so the select gesture reads identically across views.
  '[data-shift-active] &': {
    cursor: 'cell',
  },

  // Visual resize affordance on the edges. `::before`/`::after` are
  // pseudo-elements so no real DOM gets inserted — the browser treats
  // `cursor` on them the same as on any positioned element while the
  // pointer hovers their box. pointerdown still hits the bar (pseudo-
  // elements are part of their host for event routing), so
  // `detectDragZone` picks the correct handler without any change.
  // Kept at the same size as {@link EDGE_HITBOX_PX} so the cursor zone
  // matches the resize hit zone exactly — one source of truth.
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: `${EDGE_HITBOX_PX}px`,
    cursor: 'ew-resize',
    // Sit above bar content (which is position:absolute inset:0) so
    // hovering the edge zone shows the resize cursor even over the
    // label area. Stays below the peek button (z-index:2) and link
    // hotspot so those interactive affordances keep their own
    // cursors.
    zIndex: 1,
  },
  '&::before': { left: 0 },
  '&::after': { right: 0 },
});

// Inner clip layer — the text label and progress fill live here so they
// honour the bar's rounded corners and never overflow the chip, while
// the parent keeps overflow visible for the hotspot.
export const barContentStyle = css({
  position: 'absolute',
  inset: 0,
  borderRadius: 'inherit',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  padding: '0 6px',
});

// Row of display chips inside a bar. Flex container with a small gap
// between chips; the progress fill / peek button sit on top via
// absolute positioning (z-index) so the chip row can simply flow
// naturally without worrying about them. NO `paddingRight` reservation
// for the peek button — the button is a floating overlay that should
// sit ON TOP of the last chip on hover rather than permanently stealing
// 24 px of chip space even while hidden.
export const barChipRowStyle = css({
  position: 'relative',
  zIndex: 1,
  flex: '1 1 auto',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  minWidth: 0,
  pointerEvents: 'none',
});

// Base chip: inline-flex so avatar + name sit on one line, shrinkable
// with ellipsis truncation. Explicit `minWidth: 0` is what lets the
// `textOverflow: ellipsis` actually kick in inside a flex row.
export const barChipStyle = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  minWidth: 0,
  fontSize: '11px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

// Title chip — primary importance, takes remaining space and shrinks
// last. Bolder weight so even when truncated it still reads as "the
// task's name" rather than metadata.
export const barChipTitleStyle = css({
  flex: '1 1 auto',
  fontWeight: 600,
});

// Secondary chips (status, dates, progress, assignee-name-only). Fixed
// shrink order: they give up space before the title does so the bar
// always keeps the task's name legible.
export const barChipSecondaryStyle = css({
  flex: '0 1 auto',
});

// Status chip uses the select option's own colour token as a tinted
// pill. Falls back to a neutral pill when no colour is available
// (undefined select value) so the chip still reads as status.
export const barChipStatusStyle = css({
  padding: '1px 6px',
  borderRadius: '9999px',
  background: 'rgba(255,255,255,0.2)',
  fontSize: '10px',
  fontWeight: 600,
});

// Progress pill — monospace digits so "42%" / "100%" stay aligned
// when the bar is narrow and users compare percentages across rows.
export const barChipProgressStyle = css({
  fontVariantNumeric: 'tabular-nums',
  fontSize: '10px',
  fontWeight: 600,
  opacity: 0.9,
});

// Avatar inside the assignee chip. Sized down to match the 11 px
// chip row height; `flex: none` so it never shrinks — names truncate
// first, avatars stay recognisable.
export const barChipAvatarStyle = css({
  flex: 'none',
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '9px',
  fontWeight: 600,
  background: 'rgba(255,255,255,0.25)',
  color: 'var(--streamlain-v2-button-pureWhiteText)',
  overflow: 'hidden',

  img: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
});

export const milestoneStyle = css({
  position: 'absolute',
  width: `${BAR_HEIGHT}px`,
  height: `${BAR_HEIGHT}px`,
  transform: 'rotate(45deg)',
  background: 'var(--streamlain-warning-color)',
  borderRadius: '2px',
  cursor: 'pointer',
  pointerEvents: 'auto',
});

/**
 * "Open detail" peek button that sits on the right edge of the bar
 * and only appears on hover. Matches the Kanban card pattern — the
 * only UI path to the row detail panel; bar body clicks just select.
 */
export const barPeekButtonStyle = css({
  position: 'absolute',
  top: '50%',
  right: '4px',
  transform: 'translateY(-50%)',
  width: '22px',
  height: '22px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 'none',
  borderRadius: '4px',
  // Solid overlay chip sitting on top of the right-most chip on
  // hover. The chip bg uses the theme's own surface token so it's
  // theme-aware (white in light / blue-white-viewport, dark in
  // dark mode), and the icon glyph uses the theme's icon token —
  // also theme-aware. Both picked via `&&&` (spec 0,3,0) so the
  // peek chip wins over the bar's generic `&&&[data-classification]
  // *` rule (spec 0,4,0 but descendant, loses to the peek's direct
  // class match) which otherwise forces the icon white to match
  // the bar's label text and made it invisible against the white
  // chip. Using `&&&` instead of `!important` so future overrides
  // can still compose.
  background: 'var(--streamlain-background-primary-color)',
  // Icon color is set INLINE on the button + svg in bar-renderer
  // instead of here — the bar's `&&&[data-classification] *` rule
  // (spec 0,4,0) forces descendant `color: pureWhiteText` which
  // ties or beats any class-based override we can add here, and
  // `!important` in a shared control looked heavy-handed. Inline
  // style wins the cascade cleanly (spec 1,0,0).
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.18)',
  cursor: 'pointer',
  opacity: 0,
  transition: 'opacity 120ms ease-out, background 120ms ease-out',
  pointerEvents: 'auto',
  zIndex: 2,

  svg: {
    width: '14px',
    height: '14px',
  },

  [`.${barStyle}:hover &`]: {
    opacity: 1,
  },

  '&:hover': {
    background: 'var(--streamlain-hover-color)',
  },

  '&&&[data-classification=invalid-range]': {
    color: 'var(--streamlain-text-primary-color)',
    background: 'var(--streamlain-hover-color)',
  },
});

// Progress mini-bar — a 3 px strip hugging the bottom edge of the
// bar. Fill width is set inline by the renderer to `barWidth *
// progress`, so the strip grows L→R as completion increases. Design
// rationale: a full-bar overlay fought for attention with the status
// tint (bar bg already carries meaning). A thin under-rail reads as
// "progress" without competing with the base colour — matches the
// "discreet metric strip" pattern Linear/Asana use on roadmap bars.
// White at 50% alpha — 70% read too aggressive against the saturated
// status palette (the rail looked almost-opaque vs. the bar bg).
// Half-alpha keeps the rail as a discreet metric strip that lets the
// status hue bleed through without losing its "this is progress"
// readability.
export const progressFillStyle = css({
  position: 'absolute',
  left: 0,
  bottom: 0,
  height: '3px',
  borderBottomLeftRadius: 'inherit',
  borderBottomRightRadius: 'inherit',
  background: 'rgba(255, 255, 255, 0.5)',
  pointerEvents: 'none',
});

export const badgeListStyle = css({
  position: 'absolute',
  right: '4px',
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  gap: '2px',
  fontSize: '10px',
  pointerEvents: 'none',
});

export const invalidRangeIconStyle = css({
  position: 'absolute',
  right: '4px',
  top: '50%',
  transform: 'translateY(-50%)',
  color: 'var(--streamlain-error-color)',
  fontSize: '12px',
  fontWeight: 700,
});
